import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
    Dialog,
    DialogHeader,
    DialogContent,
    DialogActions,
    Button,
    ButtonSize,
    ButtonVariant,
    Spinner,
    RadioGroup,
    Radio,
} from '@shipengine/giger';

import { useSdk } from '@packlink/packlink-sdk-provider';
import { useTenantConfig } from '@packlink/tenant-config-provider';

import { setLocale } from '@store/actions';
import { getAvailableLanguages, getLocale } from '@store/selectors';

import { getLanguageSelectorListStyles, getLanguageSelectorSpinnerStyles } from './LanguageSelectorStyles';

export interface ILanguageSelectorProps {
    isOpen: boolean;
    close: () => void;
}

export const LanguageSelector = ({ isOpen, close }: ILanguageSelectorProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const PacklinkSDK = useSdk();

    const [loading, setLoading] = useState(false);
    const { availableLanguages: tenantAvailableLanguages } = useTenantConfig<ITenantConfig>();

    const currentLocale = useSelector(getLocale);
    const availableLanguages = useSelector(getAvailableLanguages);
    const [selectedLanguage, setSelectedLanguage] = useState(currentLocale);

    const availableAndSupportedLanguages = useMemo(
        () => availableLanguages.filter((language) => tenantAvailableLanguages.includes(language.locale)),
        [availableLanguages, tenantAvailableLanguages],
    );

    const handleChangeLanguage = () => {
        setLoading(true);
        const backendFormattedlanguage = selectedLanguage.replace('-', '_');

        Promise.all([
            PacklinkSDK.v1.users.updateLanguage(backendFormattedlanguage),
            PacklinkSDK.v1.clients.updateLanguage(backendFormattedlanguage),
        ])
            .then(() => {
                dispatch(setLocale(selectedLanguage));
                close();
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    return (
        <Dialog isOpen={isOpen}>
            <DialogHeader title={t('language-selector.title')} />
            <DialogContent>
                {loading ? (
                    <Spinner css={getLanguageSelectorSpinnerStyles} />
                ) : (
                    <div css={getLanguageSelectorListStyles}>
                        <RadioGroup
                            name="language"
                            value={selectedLanguage}
                            onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                            {availableAndSupportedLanguages.map((item) => (
                                <Radio key={item.locale} value={item.locale}>
                                    {item.language}
                                </Radio>
                            ))}
                        </RadioGroup>
                    </div>
                )}
            </DialogContent>
            {!loading && (
                <DialogActions>
                    <Button onClick={close} size={ButtonSize.SMALL} variant={ButtonVariant.OUTLINED}>
                        {t('language-selector.action.cancel')}
                    </Button>
                    <Button onClick={handleChangeLanguage} size={ButtonSize.SMALL}>
                        {t('language-selector.action.confirm')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
